/**
 * New Dashboard · active tenant.
 *
 * Tenants live in one canonical registry — auth-svc `tenants` — and each tenant
 * carries the capabilities it is provisioned for ("fleet", "shuttle", …). This
 * module resolves the tenants this operator may view (those with a fleet or
 * shuttle capability), remembers the choice, and renders a compact picker so the
 * registration / dispatching / drivers / device / settings pages all agree on
 * the active tenant.
 *
 * The selected tenant's **slug** is the shared key across services (no cross-DB
 * FK): shuttle-svc is addressed by slug, and it is the only identifier both
 * sides agree on.
 *
 * The two sides do NOT agree on the UUID. auth-svc `tenants` lives in the ride
 * database and shuttle `tenants` lives in fleet_meta; each mints its own id for
 * the same slug. Everything fleet-side — telemetry `tenant_id`, alerts,
 * geofences, shuttle vehicles — is keyed by the **fleet_meta** id, so that is
 * what {@link tenantId} returns. It is resolved from the slug at load time.
 * Using the auth id here silently empties the map, the KPI cards and every
 * `?tenant_id=` query, because the two UUIDs can never compare equal.
 *
 * @module features/new-dashboard/tenant
 */
import { getTenantSettings } from "../../api/shuttle.ts";
import { listTenants, type Tenant } from "../../api/tenants.ts";
import { getMe } from "../../auth.ts";
import { escapeHtml } from "../../core/ui.ts";

/** Capabilities that make a tenant relevant to the new (fleet) dashboard. */
const NEW_DASH_CAPS = ["fleet", "shuttle"];

interface NDTenant {
	slug: string;
	name: string;
	id: string;
	capabilities: string[];
}

let tenants: NDTenant[] | null = null;
let current = localStorage.getItem("nd-tenant") ?? "";
let isRoot = false;

/**
 * Fetch (and cache) the tenants this operator may view, defaulting the active
 * slug. Superadmins get the full canonical registry (filtered to fleet/shuttle
 * capable tenants); other roles fall back to just their own tenant.
 */
/**
 * Replace each tenant's auth-side UUID with its fleet_meta UUID, matched by slug.
 *
 * Fleet data is keyed by the fleet_meta id, so this is what the rest of the
 * dashboard must compare against. On failure the auth id is kept: a non-admin
 * operator cannot read the shuttle settings endpoint, and degrading to the old
 * behaviour is strictly better than dropping the tenant entirely.
 */
async function withFleetIds(list: NDTenant[]): Promise<NDTenant[]> {
	return Promise.all(
		list.map(async (t) => {
			try {
				const settings = await getTenantSettings(t.slug);
				return settings?.id ? { ...t, id: settings.id } : t;
			} catch {
				return t;
			}
		}),
	);
}

export async function loadTenants(): Promise<NDTenant[]> {
	if (!tenants) tenants = await withFleetIds(await resolveTenants());
	if ((!current || !tenants.some((t) => t.slug === current)) && tenants.length)
		current = tenants[0].slug;
	return tenants;
}

export function showTenantSelector(): boolean {
	return isRoot;
}

async function resolveTenants(): Promise<NDTenant[]> {
	const me = await getMe();
	isRoot = me?.tenant_slug === "udexon";

	if (!isRoot) {
		if (me?.tenant_slug && me.tenant_id) {
			return [
				{
					slug: me.tenant_slug,
					name: me.tenant_slug,
					id: me.tenant_id,
					capabilities: me.capabilities ?? [],
				},
			];
		}
		return [];
	}

	// Preferred source: the canonical registry (superadmin-only endpoint).
	try {
		const all = await listTenants();
		const relevant = all
			.filter((t) =>
				(t.capabilities ?? []).some((c) => NEW_DASH_CAPS.includes(c)),
			)
			.map(toND);
		if (relevant.length) return relevant;
	} catch {
		// Not a superadmin (or endpoint unavailable) — fall through to self.
	}
	// Fallback: the operator's own tenant, from the cached /me envelope.
	if (me?.tenant_slug && me.tenant_id) {
		return [
			{
				slug: me.tenant_slug,
				name: me.tenant_slug,
				id: me.tenant_id,
				capabilities: me.capabilities ?? [],
			},
		];
	}
	return [];
}

function toND(t: Tenant): NDTenant {
	return {
		slug: t.slug,
		name: t.name,
		id: t.id,
		capabilities: t.capabilities ?? [],
	};
}

/** Active tenant slug ("" until loadTenants resolves) — the cross-service key. */
export function tenantSlug(): string {
	return current;
}

/** Canonical tenant id (UUID) for the active slug ("" until resolved). */
export function tenantId(): string {
	return tenants?.find((t) => t.slug === current)?.id ?? "";
}

/** Enabled capabilities of the active tenant. */
export function tenantCapabilities(): string[] {
	return tenants?.find((t) => t.slug === current)?.capabilities ?? [];
}

/** Whether the active tenant has a given capability. */
export function tenantHasCapability(cap: string): boolean {
	return tenantCapabilities().includes(cap);
}

export function setTenantSlug(slug: string): void {
	current = slug;
	localStorage.setItem("nd-tenant", slug);
}

/** `<option>` markup for the tenant picker; embed inside a `<select>`. */
export function tenantOptionsHtml(): string {
	return (tenants ?? [])
		.map(
			(t) =>
				`<option value="${escapeHtml(t.slug)}" ${t.slug === current ? "selected" : ""}>${escapeHtml(t.name)}</option>`,
		)
		.join("");
}
